import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API_BASE_URL as API } from '../config/api';
import {
  ArrowLeft, Bell, Heart, MessageCircle, UserPlus, Swords,
  DollarSign, Radio, Megaphone, Shield, Loader2, Save
} from 'lucide-react';

const SETTING_SECTIONS = [
  {
    title: "Social",
    icon: UserPlus,
    settings: [
      { key: "follows", label: "New followers", desc: "When someone follows you" },
      { key: "mentions", label: "Mentions", desc: "When someone mentions you in a post" },
    ]
  },
  {
    title: "Engagement",
    icon: Heart,
    settings: [
      { key: "reactions", label: "Reactions", desc: "When someone reacts to your content" },
      { key: "comments", label: "Comments", desc: "When someone comments on your post" },
      { key: "reposts", label: "Reposts", desc: "When someone reposts your content" },
    ]
  },
  {
    title: "Live & Battles",
    icon: Swords,
    settings: [
      { key: "live_alerts", label: "Live alerts", desc: "When someone you follow goes live" },
      { key: "battle_invites", label: "Battle invites", desc: "When you receive a battle invite" },
      { key: "battle_results", label: "Battle results", desc: "Win/loss notifications" },
    ]
  },
  {
    title: "Earnings",
    icon: DollarSign,
    settings: [
      { key: "earnings_alerts", label: "Earnings alerts", desc: "When you earn money" },
      { key: "purchase_alerts", label: "Purchase alerts", desc: "When someone buys your content" },
      { key: "payout_alerts", label: "Payout alerts", desc: "When payouts are processed" },
    ]
  },
  {
    title: "System",
    icon: Megaphone,
    settings: [
      { key: "announcements", label: "Platform announcements", desc: "Feature updates and news" },
      { key: "promotions", label: "Promotions", desc: "Promotional content and offers" },
      { key: "moderation", label: "Moderation alerts", desc: "Content moderation notices" },
    ]
  },
];

export default function NotificationSettingsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});
  const [quietHours, setQuietHours] = useState({ enabled: false, start: "22:00", end: "08:00" });

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/notifications/settings`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const cats = data.categories || {};
        const merged = {};
        SETTING_SECTIONS.forEach(section => {
          section.settings.forEach(s => {
            merged[s.key] = cats[s.key] !== undefined ? cats[s.key] : true;
          });
        });
        setSettings(merged);
        setQuietHours({
          enabled: data.quiet_hours_enabled || false,
          start: data.quiet_hours_start || "22:00",
          end: data.quiet_hours_end || "08:00",
        });
      }
    } catch (err) {
      console.error("Failed to fetch notification settings:", err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchSettings();
  }, [isAuthenticated, navigate, fetchSettings]);

  const toggleSetting = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/notifications/settings`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categories: settings,
          quiet_hours_enabled: quietHours.enabled,
          quiet_hours_start: quietHours.start,
          quiet_hours_end: quietHours.end,
        }),
      });
      if (res.ok) {
        toast.success('Notification settings saved');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="notification-settings-page">
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-[#0A0A0A]/70 border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white hover:bg-white/5" data-testid="notif-settings-back">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">Notification Settings</h1>
              <p className="text-xs text-zinc-500">Control what you get notified about</p>
            </div>
          </div>
          <Button onClick={saveSettings} disabled={saving} className="bg-[#007AFF] hover:bg-[#0066DD] text-white" data-testid="save-notif-settings">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {SETTING_SECTIONS.map((section) => {
          const SectionIcon = section.icon;
          return (
            <Card key={section.title} className="bg-[#141414] border-white/10 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
                <SectionIcon className="w-4 h-4 text-[#007AFF]" />
                <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">{section.title}</h3>
              </div>
              <div className="divide-y divide-white/5">
                {section.settings.map((s) => (
                  <div key={s.key} className="px-5 py-3.5 flex items-center justify-between" data-testid={`notif-toggle-${s.key}`}>
                    <div>
                      <p className="text-sm font-medium">{s.label}</p>
                      <p className="text-xs text-zinc-500">{s.desc}</p>
                    </div>
                    <Switch
                      checked={settings[s.key] !== false}
                      onCheckedChange={() => toggleSetting(s.key)}
                      className="data-[state=checked]:bg-[#007AFF]"
                    />
                  </div>
                ))}
              </div>
            </Card>
          );
        })}

        {/* Quiet Hours */}
        <Card className="bg-[#141414] border-white/10 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#007AFF]" />
            <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Quiet Hours</h3>
          </div>
          <div className="px-5 py-4 flex items-center justify-between" data-testid="quiet-hours-toggle">
            <div>
              <p className="text-sm font-medium">Enable quiet hours</p>
              <p className="text-xs text-zinc-500">Mute notifications during set hours</p>
            </div>
            <Switch
              checked={quietHours.enabled}
              onCheckedChange={(v) => setQuietHours(prev => ({ ...prev, enabled: v }))}
              className="data-[state=checked]:bg-[#007AFF]"
            />
          </div>
          {quietHours.enabled && (
            <div className="px-5 pb-4 flex gap-4">
              <div className="flex-1">
                <label className="text-xs text-zinc-500 block mb-1">Start</label>
                <input
                  type="time"
                  value={quietHours.start}
                  onChange={(e) => setQuietHours(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-zinc-500 block mb-1">End</label>
                <input
                  type="time"
                  value={quietHours.end}
                  onChange={(e) => setQuietHours(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
